import clsx from 'clsx'
import {findIndex, isEmpty} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {
  TIMEZONES,
  ROUTES,
  TABLET_BREAKPOINT,
  MOBILE_MAX_WIDTH,
} from '../../../../../../_gori/constants'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import displayConfig from '../../../../../../displayconfig.json'
import {useAuth} from '../../../../auth'
import {
  ClaimAlertModal,
  TimeZoneModal,
  LanguageModal,
  CustomerTrackingNotificationModal,
} from '../../../../settings'
import {ReportSettingModal} from '../../../modals/account/general/ReportSettingModal'
import {ConfigGeneral, SettingsConfig} from '../../../core/_const'
import SettingsService from '../../../core/_requests'
import UseRoutesPermission from '../../../../../../_gori/hooks/UseRoutesPermission'
import {LanguageConfig} from '../../../../../../_gori/config/LanguageConfig'
import {useLanguage} from '../../../../../../_gori/i18n/Metronici18n'
import {PageTitle} from '../../../../../../_gori/layout/core'
import useWindowDimensions from '../../../../../../_gori/hooks/useWindowDimensions'

type GeneralTab = {
  key: string
  label: string
  to: string
}

const GeneralSegmentNav: React.FC<{tabs: GeneralTab[]; activeTab: string | null}> = ({
  tabs,
  activeTab,
}) => {
  const intl = useIntl()

  return (
    <nav className='settings-segment-nav settings-segment-nav--general'>
      {tabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          className={clsx('settings-segment-nav__item', {
            active: activeTab === tab.key,
          })}
        >
          {intl.formatMessage({id: tab.label})}
        </Link>
      ))}
    </nav>
  )
}

const GeneralPage: React.FC = () => {
  const intl = useIntl()
  const {routes} = UseRoutesPermission()
  const {newCancelToken, isCancel} = useCancelToken()
  const {currentUser} = useAuth()
  const {language} = useLanguage()
  const [searchParams] = useSearchParams()
  const [showModal, setShowModal] = useState<any>({
    language: false,
    time_zone: false,
    customer_tracking_notifications: false,
    claim_alerts: false,
    report_settings: false,
  })
  const [dataNotification, setDataNotification] = useState<{
    meter: any
    claims: any
    customer_tracking: any
  }>({
    meter: undefined,
    claims: undefined,
    customer_tracking: undefined,
  })
  const [isLoading, setIsLoading] = useState(false)
  const {width} = useWindowDimensions()

  const nameTab = useMemo(() => {
    const tabInit = ConfigGeneral.NAME_TAB.NOTIFICATIONS
    return searchParams.get('type') ? searchParams.get('type') : tabInit
  }, [searchParams])

  const timeZone = useMemo(() => {
    let timezone = currentUser?.user_profiles?.general?.timezone?.value ?? null
    let key = findIndex(TIMEZONES, (e) => e.value === timezone)
    return TIMEZONES[key]?.label ?? null
  }, [currentUser?.user_profiles?.general?.timezone?.value])

  const currentLanguage = useMemo(() => {
    return LanguageConfig().LANGUAGES.find((x) => x.value === language) ?? null
  }, [language])

  const generalTabs = useMemo(() => {
    const tabs = [
      {
        key: ConfigGeneral.NAME_TAB.NOTIFICATIONS,
        label: 'NOTIFICATIONS',
        to: `${ROUTES.SETTINGS.GENERAL}?type=${ConfigGeneral.NAME_TAB.NOTIFICATIONS}`,
      },
      {
        key: ConfigGeneral.NAME_TAB.LANGUAGE,
        label: 'LANGUAGE',
        to: `/settings/general?type=${ConfigGeneral.NAME_TAB.LANGUAGE}`,
      },
      {
        key: SettingsConfig.REPORTS_SETTING.REPORT.value,
        label: 'REPORTS',
        to: `/settings/general?type=${SettingsConfig.REPORTS_SETTING.REPORT.value}`,
      },
    ]

    if (displayConfig.items.settings.timezone) {
      tabs.push({
        key: ConfigGeneral.NAME_TAB.TIME_ZONE,
        label: 'TIME_ZONE',
        to: `/settings/general?type=${ConfigGeneral.NAME_TAB.TIME_ZONE}`,
      })
    }

    return tabs
  }, [])

  const buildHtml = useCallback(
    (config) => {
      return (
        <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
          <div>
            <div className='d-flex justify-content-between'>
              <span className='fs-3 fw-bolder text-gray-700'>
                {intl.formatMessage({id: config.title})}
              </span>
              {config.name !== 'report_settings' && (
                <div
                  className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder'
                  onClick={() => setShowModal((prev) => ({...prev, [config.name]: true}))}
                >
                  {intl.formatMessage({id: 'EDIT'})}
                </div>
              )}
            </div>
            {config?.subtitle && (
              <p className='form-label text-gray-600 mb-0'>{config?.subtitle}</p>
            )}
          </div>
          <div className='mt-6'>{config.children}</div>
        </div>
      )
    },
    [intl]
  )

  const buildSkeleton = useMemo(() => {
    return (
      <div className='mt-6 w-100 w-sm-75'>
        <div className='d-flex justify-content-center'>
          <div className='w-100 bg-light rounded p-8'>
            <div className='d-flex justify-content-between'>
              <div className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
              <span className='fs-1 col-1 placeholder rounded-2 bg-secondary' />
            </div>
            <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
            <div className='col-12 d-flex justify-content-between'>
              <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
              <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
            </div>
            <div className='col-12 d-flex justify-content-between mt-6'>
              <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
              <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
            </div>
          </div>
        </div>
      </div>
    )
  }, [])

  const buildConfig = useMemo(() => {
    const dataClaims = dataNotification.claims
    const dataTracking = dataNotification.customer_tracking

    let _obj: any = {
      LANGUAGE: {
        title: 'PREFERRED_LANGUAGE',
        name: 'language',
        subtitle: intl.formatMessage({id: 'LANGUAGE_TEXT'}),
        children: (
          <div className='d-flex text-gray-700 fs-5 mt-8 general-setting-row general-setting-row--spread'>
            <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'LANGUAGE'})}</div>
            <div className='text-wrap fw-bolder'>{currentLanguage?.label}</div>
          </div>
        ),
      },
      TIME_ZONE: {
        title: 'TIME_ZONE',
        name: 'time_zone',
        subtitle: intl.formatMessage({id: 'TIME_ZONE_TEXT'}),
        children: (
          <div className='d-flex text-gray-700 fs-5 mt-8 general-setting-row general-setting-row--spread'>
            <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'TIME_ZONE'})}</div>
            <div className='text-wrap fw-bolder'>{timeZone}</div>
          </div>
        ),
      },
      REPORT_SETTINGS: {
        title: 'REPORT_SETTINGS',
        name: 'report_settings',
        subtitle: intl.formatMessage({id: 'WEIGHT_TEXT'}),
        children: <ReportSettingModal />,
      },
    }

    if (routes.NOTIFICATIONS_CLAIMS.hasPermission) {
      _obj = {
        ..._obj,
        CLAIM_ALERTS: {
          title: 'CLAIM_ALERTS',
          name: 'claim_alerts',
          subtitle: intl.formatMessage({id: 'CLAIM_ALERTS_TEXT'}),
          children: dataClaims?.status ? (
            <div className='d-flex text-gray-700 fs-5 mt-8 general-setting-row general-setting-row--tablet-stack'>
              <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'EMAIL_TO'})}</div>
              <div className='text-wrap fw-bolder general-setting-row__value'>
                {dataClaims?.receiver?.email?.map((email, index) => (
                  <div
                    className={clsx(index === 0 ? 'text-truncate' : 'text-truncate mt-2')}
                    key={index}
                  >
                    {email}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className='d-flex text-gray-700 fs-5 mt-8'>
              <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'ALERT'})}</div>
              <div className='text-wrap fw-bolder'>
                {intl.formatMessage({id: 'OFF'}).toUpperCase()}
              </div>
            </div>
          ),
        },
      }
    }

    if (routes.NOTIFICATIONS_CUSTOMER_TRACKING.hasPermission) {
      _obj = {
        ..._obj,
        CUSTOMER_TRACKING_NOTIFICATIONS: {
          title: 'CUSTOMER_TRACKING_NOTIFICATIONS',
          name: 'customer_tracking_notifications',
          subtitle: intl.formatMessage({id: 'CUSTOMER_TRACKING_TEXT'}),
          children: dataTracking?.status ? (
            <div className='d-flex text-gray-700 fs-5 mt-8 general-setting-row'>
              <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'COMPANY'})}</div>
              <div className='text-wrap fw-bolder text-truncate general-setting-row__value'>
                {dataTracking?.value?.name}
              </div>
            </div>
          ) : (
            <div className='d-flex text-gray-700 fs-5 mt-8'>
              <div className='col-6 col-sm-4 fw-bold'>{intl.formatMessage({id: 'ALERT'})}</div>
              <div className='text-wrap fw-bolder'>
                {intl.formatMessage({id: dataTracking?.status ? 'ON' : 'OFF'}).toUpperCase()}
              </div>
            </div>
          ),
        },
      }
    }

    return _obj
  }, [currentLanguage, dataNotification, intl, routes, timeZone])

  const getDataNotification = useCallback(
    async (name?: string) => {
      try {
        const config = {
          params: {
            type: name,
          },
          cancelToken: newCancelToken(),
        }
        setIsLoading(true)
        const {notifications} = await SettingsService.getNotification(config)
        const data = isEmpty(notifications)
          ? {}
          : notifications.reduce((total, current: any) => {
              total[current?.category] = current
              return total
            }, {})
        setDataNotification(data)
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setIsLoading(false)
      }
    },
    [isCancel, newCancelToken]
  )

  useEffect(() => {
    getDataNotification()
  }, [getDataNotification])

  const renderTabContent = () => {
    if (isLoading) {
      return Object.entries(buildConfig).map(([, config], index) => (
        <React.Fragment key={index}>{buildSkeleton}</React.Fragment>
      ))
    }

    return Object.entries(buildConfig).map(([key, config]) => {
      switch (key) {
        case 'LANGUAGE':
          return nameTab === ConfigGeneral.NAME_TAB.LANGUAGE ? buildHtml(config) : null
        case 'TIME_ZONE':
          return nameTab === ConfigGeneral.NAME_TAB.TIME_ZONE ? buildHtml(config) : null
        case 'REPORT_SETTINGS':
          return nameTab === SettingsConfig.REPORTS_SETTING.REPORT.value ? buildHtml(config) : null
        default:
          return nameTab === ConfigGeneral.NAME_TAB.NOTIFICATIONS ? buildHtml(config) : null
      }
    })
  }

  return (
    <>
      <PageTitle
        trailingActions={
          width < TABLET_BREAKPOINT && width > MOBILE_MAX_WIDTH ? (
            <GeneralSegmentNav tabs={generalTabs} activeTab={nameTab} />
          ) : undefined
        }
      >
        {`${intl.formatMessage({id: 'SETTINGS'})} > ${intl.formatMessage({id: 'GENERAL'})}`}
      </PageTitle>
      {showModal.language && (
        <LanguageModal
          show={showModal.language}
          handleClose={() => {
            setShowModal((prev) => ({...prev, language: false}))
          }}
        />
      )}
      {showModal.time_zone && (
        <TimeZoneModal
          show={showModal.time_zone}
          handleClose={() => {
            setShowModal((prev) => ({...prev, time_zone: false}))
          }}
        />
      )}
      {showModal.customer_tracking_notifications && (
        <CustomerTrackingNotificationModal
          show={showModal.customer_tracking_notifications}
          handleClose={() => {
            setShowModal((prev) => ({...prev, customer_tracking_notifications: false}))
          }}
          handleReload={() => getDataNotification()}
          data={dataNotification.customer_tracking}
        />
      )}
      {showModal.claim_alerts && (
        <ClaimAlertModal
          show={showModal.claim_alerts}
          handleClose={() => {
            setShowModal((prev) => ({...prev, claim_alerts: false}))
          }}
          handleReload={() => getDataNotification()}
          data={dataNotification.claims}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='mb-4'>
          <div className='d-flex flex-column align-items-center justify-content-center'>
            <div className='general-page-header settings-header w-100 d-flex flex-wrap justify-content-between align-items-center gap-4 mt-6'>
              <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1 section-title'>
                {intl.formatMessage({id: 'GENERAL'})}
              </h1>
              <GeneralSegmentNav tabs={generalTabs} activeTab={nameTab} />
            </div>
            {renderTabContent()}
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {GeneralPage}
